import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReactFlowProvider } from "@xyflow/react";
import CustomSpinner from "components/display/CustomSpinner";
import { createApi } from "util/ApiCreator";
import { ValidationTypeResponse, FileResponse, EmailTemplateResponse, BankAccountResponse, ProcessResponse, StepCreateRequest, ProcessCreateRequest, StepResponse } from "api/main/data-contracts";
import { BankAccountController } from "api/main/BankAccountController";
import { EmailTemplateAdminController } from "api/main/EmailTemplateAdminController";
import ProcessVisualBuilder from "./ProcessVisualBuilder";

export interface MetaInformation {
  stepTypes?: string[];
  inputTypes?: string[];
  validatorTypes?: ValidationTypeResponse[];
  signTypes?: string[];
  stepExecutors?: string[];
  notificationTypes?: string[];
  files?: FileResponse[];
  emailTemplateTypes?: EmailTemplateResponse[];
  bankAccounts?: BankAccountResponse[];
}

export default function ProcessPage() {
  const api = useRef(createApi("process"))
  const fileApi = useRef(createApi("filestorage"))

  const emailTemplateAPI = useRef(createApi("emailtemplate") as EmailTemplateAdminController)
  const bankAccountApi = useRef(createApi("bankaccount") as BankAccountController)

  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [metaInfo, setMetaInfo] = useState<MetaInformation>();
  const [process, setProcess] = useState<ProcessResponse>();

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = async () => {
    setLoading(true);

    const stepTypes = await api.current.findAllStepTypes();
    const inputTypes = await api.current.findAllInputTypes();
    const validatorTypes = await api.current.findAllValidatorTypes();
    const signTypes = await api.current.findAllSignTypes();
    const executors = await api.current.findAllStepExecutors();
    const notificationTypes = await api.current.findAllNotificationTypes();
    const files = await fileApi.current.findAll();
    const emailTemplates = await emailTemplateAPI.current.findAll();
    const bankAccounts = await bankAccountApi.current.findAll();

    if (id) {
      let process = await api.current.findById(+id);
      setProcess(process);
    }

    setMetaInfo({
      stepTypes: stepTypes,
      inputTypes: inputTypes,
      validatorTypes: validatorTypes,
      signTypes: signTypes,
      stepExecutors: executors,
      notificationTypes: notificationTypes,
      files: files,
      emailTemplateTypes: emailTemplates,
      bankAccounts: bankAccounts,
    });
    setLoading(false);
  };

  async function handleFormikSubmit(values: FormValues) {
    const createRequest = toCreateRequest(values);
    if (process && process.id) {
      await api.current.update(
        process?.id,
        { id: process.id, ...createRequest },
        {},
      );
    } else {
      await api.current.create(createRequest, {});
    }
    navigate("/process");
  }

  const handleExport = (values: FormValues) => {
    alert(
      "Prašome patikrinti consolę. Joje rasite žinutę su generuotu proceso requestu.",
    );
    console.log(JSON.stringify(toCreateRequest(values)));
  };

  const handleImport = async (setValues: any, setFieldValue: any) => {
    const request = prompt(
      "Prašome įkopijuoti JSON tipo duomenis:",
    );
    if (request === null || request === "") {
      alert("Neteisingi duomenys");
    } else {
      try {
        const createRequest = JSON.parse(request);
        await setValues(
          getInitialValues(createRequest),
        );
        await setFieldValue("imported", false);
      } catch (e) {
        alert("Neteisingi JSON duomenys");
      }
    }
  };

  if (loading) {
    return <CustomSpinner label={"Prašome palaukti. Kraunama..."} />;
  }

  return (
    <ReactFlowProvider>
      <ProcessVisualBuilder
        process={process}
        metaInfo={metaInfo}
        onSubmit={handleFormikSubmit}
        onExport={handleExport}
        onImport={handleImport}
        isNewProcess={!id}
      />
    </ReactFlowProvider>
  );
}

export function getInitialValues(process?: ProcessCreateRequest): ProcessCreateRequest {
  return {
    i18nkey: process?.i18nkey ?? "",
    name: process?.name ?? "",
    active: process?.active ?? false,
    steps:
      process?.steps?.map((step: StepResponse) => getStepInitialValues(step)) ??
      [],
  };
}
export type FormValues = ReturnType<typeof getInitialValues>;

export function getStepInitialValues(step?: StepResponse, stepIndex?: number): StepCreateRequest {
  let dto = {
    i18nkey: step?.i18nkey ?? "",
    name: step?.name ?? "",
    description: step?.description ?? "",
    stepInformation: {
      inputContent: step?.stepInformation?.inputContent,
      signContent: step?.stepInformation?.signContent,
      paymentContent: step?.stepInformation?.paymentContent,
      stripePaymentContent: step?.stepInformation?.stripePaymentContent ?? {
        amount: 0,
        currency: "EUR",
        description: "",
        paymentType: "ONE_TIME",
        stripePriceId: "",
      },
      generateDocumentContent: step?.stepInformation?.generateDocumentContent,
      confirmationContent: step?.stepInformation?.confirmationContent,
      notificationContent: step?.stepInformation?.notificationContent,
      uploadDocumentContent: step?.stepInformation?.uploadDocumentContent,
    },
    type: step?.type,
    executors: step?.executors ?? [],
    notificationType: step?.notificationType ?? "NONE",
    emailTemplateId: step?.emailTemplate?.id
  };
  return dto;
}
export type StepValues = ReturnType<typeof getStepInitialValues>;

function cleanStepInformation(step: StepCreateRequest) {
  if (!step.stepInformation) return;

  // Helper to clear all except specified fields
  const clearAllExcept = (...keep: string[]) => {
    const allFields = [
      'inputContent', 'signContent', 'paymentContent', 'stripePaymentContent',
      'generateDocumentContent', 'confirmationContent', 'notificationContent', 'uploadDocumentContent'
    ];
    allFields.forEach(field => {
      if (!keep.includes(field)) {
        (step.stepInformation as any)[field] = undefined;
      }
    });
  };

  switch (step.type) {
    case "DATA_INPUT":
      clearAllExcept('inputContent');
      break;
    case "SIGN":
      clearAllExcept('signContent');
      break;
    case "PAYMENT":
      clearAllExcept('paymentContent');
      break;
    case "STRIPE_PAYMENT":
      clearAllExcept('stripePaymentContent');
      break;
    case "GENERATE_DOCUMENT":
      clearAllExcept('generateDocumentContent');
      break;
    case "CONFIRMATION":
      clearAllExcept('confirmationContent');
      break;
    case "NOTIFICATION":
      clearAllExcept('notificationContent');
      break;
    case "IDENTIFICATION":
      clearAllExcept(); // No specific content needed
      break;
    default:
      clearAllExcept(); // Clear all for unknown types
      break;
  }
}
export function toCreateRequest(values: FormValues): ProcessCreateRequest {
  values?.steps?.forEach((step: StepCreateRequest) => {
    step.stepInformation?.inputContent?.forEach(group => {
      group.forEach(input => {
        if (!input || !input.validations)
          return;
      })
    })

    cleanStepInformation(step);
  });

  return {
    name: values.name,
    i18nkey: values.i18nkey,
    active: values.active,
    steps:
      values.steps &&
      values.steps.map(
        (step: StepValues): StepCreateRequest => ({
          i18nkey: step?.i18nkey,
          name: step?.name,
          description: step?.description,
          stepInformation: step?.stepInformation ?? undefined,
          type: step?.type,
          executors: step?.executors,
          notificationType: step?.notificationType ?? "NONE",
          emailTemplateId: step?.emailTemplateId ?? undefined,
        }),
      ),
  };
}
